import { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Form, Modal, ProgressBar, Table } from 'react-bootstrap';
import { useClientFlags } from '@/hooks/useClientFlags';
import { clientMetadataService } from '@/services/clientMetadataService';
import './clientFlags.css';

export type BulkInactiveAction = 'mark-inactive' | 'mark-active';

interface BulkInactiveDialogProps {
  /** What the run does. Null closes the dialog. */
  action: BulkInactiveAction | null;
  /** The clients ticked on the page. */
  clientNames: string[];
  onClose: () => void;
  /** Called when the dialog closes after a run, with the clients whose tag changed. */
  onDone?: (changed: string[]) => void;
}

type Outcome = 'done' | 'already' | 'failed';

interface Result {
  name: string;
  outcome: Outcome;
  detail?: string;
}

/** How many clients are written at once. Each write is one small call, so a few at a time is quick and gentle. */
const WORKERS = 4;

const plural = (n: number, one: string, many: string) => `${n.toLocaleString()} ${n === 1 ? one : many}`;

/**
 * Mark many clients inactive, or active again, in one go. It lists the names
 * first and writes nothing until the person confirms. Each client is one
 * write of its own, so one failure never stops the rest, and the failed ones
 * can be tried again.
 */
export function BulkInactiveDialog({ action, clientNames, onClose, onDone }: BulkInactiveDialogProps) {
  const flags = useClientFlags();
  // The run keeps the action and the names it opened with, whatever the page behind it does.
  const [run, setRun] = useState<{ action: BulkInactiveAction; names: string[] } | null>(null);
  const [reason, setReason] = useState('');
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [results, setResults] = useState<Result[] | null>(null);

  useEffect(() => {
    if (!action) return;
    setRun({ action, names: [...clientNames].sort((a, b) => a.localeCompare(b)) });
    setReason('');
    setResults(null);
    setHeldPlan(null);
    setProgress({ done: 0, total: 0 });
    // The names are read once, when the dialog opens.
  }, [action]);

  const marking = run?.action !== 'mark-active';
  const names = useMemo(() => run?.names ?? [], [run]);

  // Before the run: who changes, and who already stands that way. The list is held from the
  // moment the run starts, because the tags on screen change under it as each write lands.
  const livePlan = useMemo(
    () => names.map((name) => ({ name, skip: flags.isInactive(name) === marking })),
    [names, flags, marking]
  );
  const [heldPlan, setHeldPlan] = useState<typeof livePlan | null>(null);
  const plan = heldPlan ?? livePlan;
  const toChange = plan.filter((row) => !row.skip).map((row) => row.name);

  const execute = async (targets: string[], reasonText: string, previous: Result[]) => {
    if (!run || targets.length === 0) return;
    setRunning(true);
    setProgress({ done: 0, total: targets.length });
    const queue = [...targets];
    const fresh: Result[] = [];
    let done = 0;
    const worker = async () => {
      for (let name = queue.shift(); name; name = queue.shift()) {
        try {
          const result = marking
            ? await clientMetadataService.setInactive(name, reasonText)
            : await clientMetadataService.liftInactive(name);
          fresh.push({ name, outcome: result.changed ? 'done' : 'already' });
        } catch (e) {
          fresh.push({ name, outcome: 'failed', detail: e instanceof Error ? e.message : String(e) });
        }
        done += 1;
        setProgress({ done, total: targets.length });
      }
    };
    await Promise.all(Array.from({ length: Math.min(WORKERS, targets.length) }, worker));
    const retried = new Set(targets);
    const merged = [...previous.filter((r) => !retried.has(r.name)), ...fresh].sort((a, b) =>
      a.name.localeCompare(b.name)
    );
    setResults(merged);
    setRunning(false);
  };

  const start = () => {
    setHeldPlan(plan);
    const skipped: Result[] = plan.filter((row) => row.skip).map((row) => ({ name: row.name, outcome: 'already' }));
    void execute(toChange, reason, skipped);
  };

  const failed = results?.filter((r) => r.outcome === 'failed') ?? [];
  const retry = () =>
    void execute(
      failed.map((r) => r.name),
      reason,
      results ?? []
    );

  const close = () => {
    const changed = results?.filter((r) => r.outcome === 'done').map((r) => r.name) ?? [];
    if (results) onDone?.(changed);
    onClose();
  };

  const alreadyWord = marking ? 'Already inactive' : 'Already active';
  const doneCount = results?.filter((r) => r.outcome === 'done').length ?? 0;
  const alreadyCount = results?.filter((r) => r.outcome === 'already').length ?? 0;

  return (
    <Modal show={Boolean(action)} onHide={running ? undefined : close} centered>
      <Modal.Header closeButton={!running}>
        <Modal.Title>
          {marking
            ? `Mark ${plural(toChange.length || names.length, 'client', 'clients')} inactive?`
            : `Mark ${plural(toChange.length || names.length, 'client', 'clients')} active again?`}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!results ? (
          <>
            <p className="client-flag-dialog__effect">
              {marking
                ? "Each client below leaves the client pickers, the All Production Clients group, Home's counts and the Credits table, unless a person asks to see inactive clients. Nothing changes in any workspace."
                : "Each client below goes back into the client pickers, the All Production Clients group, Home's counts and the Credits table."}
            </p>
            <div className="client-flag-dialog__list">
              <Table size="sm">
                <tbody>
                  {plan.map((row) => (
                    <tr key={row.name} className={row.skip ? 'client-flag-dialog__skip' : undefined}>
                      <td className="client-flag-dialog__name">{row.name}</td>
                      <td className="client-flag-dialog__detail">{row.skip ? `${alreadyWord}: no change` : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
            {running ? (
              <div className="client-flag-dialog__progress">
                <ProgressBar now={progress.total ? (100 * progress.done) / progress.total : 0} />
                <span className="numeric">
                  {progress.done} of {progress.total} done
                </span>
              </div>
            ) : (
              marking && (
                <Form.Group controlId="bulk-inactive-reason">
                  <Form.Label>Reason (optional)</Form.Label>
                  <Form.Control
                    type="text"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Trial ended and the customer did not go on"
                  />
                  <Form.Text>One reason, saved on every client above.</Form.Text>
                </Form.Group>
              )
            )}
          </>
        ) : (
          <>
            <p className="client-flag-dialog__outcome">
              {plural(doneCount, 'client', 'clients')} marked {marking ? 'inactive' : 'active again'}
              {alreadyCount > 0 && `, ${alreadyCount.toLocaleString()} ${alreadyWord.toLowerCase()}`}
              {failed.length > 0 && `, ${failed.length.toLocaleString()} failed`}.
            </p>
            <div className="client-flag-dialog__list">
              <Table size="sm">
                <tbody>
                  {results.map((r) => (
                    <tr key={r.name}>
                      <td className="client-flag-dialog__name">{r.name}</td>
                      <td>
                        {r.outcome === 'done' ? (
                          <Badge bg="success">Done</Badge>
                        ) : r.outcome === 'already' ? (
                          <Badge bg="secondary">{alreadyWord}</Badge>
                        ) : (
                          <Badge bg="danger">Failed</Badge>
                        )}
                      </td>
                      <td className="client-flag-dialog__detail">{r.detail ?? ''}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
            {running && (
              <div className="client-flag-dialog__progress">
                <ProgressBar now={progress.total ? (100 * progress.done) / progress.total : 0} />
                <span className="numeric">
                  {progress.done} of {progress.total} done
                </span>
              </div>
            )}
            {failed.length > 0 && !running && (
              <Alert variant="danger" className="mb-0">
                {plural(failed.length, 'client was', 'clients were')} not changed. Nothing else was touched. Try{' '}
                {failed.length === 1 ? 'it' : 'them'} again.
              </Alert>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {!results ? (
          <>
            <Button variant="outline-secondary" onClick={close} disabled={running}>
              Cancel
            </Button>
            <Button variant="primary" onClick={start} disabled={running || toChange.length === 0}>
              {running
                ? 'Working'
                : toChange.length === 0
                  ? 'Nothing to change'
                  : marking
                    ? `Mark ${toChange.length.toLocaleString()} inactive`
                    : `Mark ${toChange.length.toLocaleString()} active again`}
            </Button>
          </>
        ) : (
          <>
            {failed.length > 0 && (
              <Button variant="outline-secondary" onClick={retry} disabled={running}>
                Try the {failed.length.toLocaleString()} failed again
              </Button>
            )}
            <Button variant="primary" onClick={close} disabled={running}>
              Close
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
}

export default BulkInactiveDialog;
